import React from 'react';
import { Pencil, Trash2, Clock } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import type { Oppmote } from '@/services/api/types';
import { formatTime, formatMinuterFør } from '../utils/timeUtils';

interface OppmoteTableProps {
  oppmote: Oppmote[];
  readOnly: boolean;
  onEdit: (item: Oppmote) => void;
  onDelete: (id: number) => void;
}

export const OppmoteTable: React.FC<OppmoteTableProps> = ({
  oppmote,
  readOnly,
  onEdit,
  onDelete,
}) => {
  return (
    <div className="border rounded-md">
      <Table>
        <TableHeader>
          <TableRow className="bg-slate-50 hover:bg-slate-50">
            <TableHead className="w-[25%]">Navn</TableHead>
            <TableHead className="w-[20%]">Tidspunkt</TableHead>
            <TableHead className="w-[20%]">Relativ tid</TableHead>
            <TableHead className="w-[25%]">Talenter</TableHead>
            <TableHead className="w-[10%] text-right">Handlinger</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {oppmote.map((item) => (
            <TableRow key={item.id}>
              <TableCell>
                <div>
                  <div className="font-medium">{item.navn}</div>
                  {item.beskrivelse && (
                    <div className="text-sm text-muted-foreground">{item.beskrivelse}</div>
                  )}
                </div>
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-muted-foreground" />
                  {formatTime(item.tidspunkt, 'HH:mm')}
                </div>
              </TableCell>
              <TableCell>
                <Badge variant={item.er_relativ ? "outline" : "secondary"}>
                  {formatMinuterFør(item)}
                </Badge>
              </TableCell>
              <TableCell>
                <div className="flex flex-wrap gap-1">
                  {item.talenter && item.talenter.length > 0 ? (
                    item.talenter.map((talent, idx) => {
                      const isGroup = talent.er_gruppe;

                      return (
                        <Badge key={idx} variant="secondary" className="text-xs">
                          {isGroup ? (
                            <>
                              {talent.talent_navn} <span className="text-muted-foreground">(gruppe)</span>
                            </>
                          ) : (
                            talent.talent_navn
                          )}
                        </Badge>
                      );
                    })
                  ) : (
                    <span className="text-xs text-muted-foreground">Alle</span>
                  )}
                </div>
              </TableCell>
              <TableCell className="text-right">
                {!readOnly && (
                  <div className="flex justify-end gap-2">
                    <Button
                      onClick={() => onEdit(item)}
                      variant="ghost"
                      size="sm"
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      onClick={() => onDelete(item.id)}
                      variant="ghost"
                      size="sm"
                      className="text-destructive hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

